// src/components/MarketInsights/components/SpecialsFeed.tsx

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Skeleton,
  useTheme,
  alpha,
} from "@mui/material";
import { LocalOffer as SpecialsIcon } from "@mui/icons-material";
import { SpecialItem } from "../types";

interface SpecialsFeedProps {
  data: SpecialItem[] | undefined;
  isLoading: boolean;
}

const SpecialsFeed: React.FC<SpecialsFeedProps> = ({ data, isLoading }) => {
  const theme = useTheme();

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <SpecialsIcon
            sx={{ color: theme.palette.warning.main, fontSize: 20 }}
          />
          <Typography variant="subtitle1" fontWeight={700}>
            Current specials
          </Typography>
          {data && (
            <Chip
              label={data.length}
              size="small"
              sx={{
                ml: "auto",
                fontWeight: 600,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
              }}
            />
          )}
        </Box>
      </CardContent>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          pb: 2,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.primary, 0.15),
            borderRadius: 2,
          },
        }}
      >
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                <Skeleton variant="rounded" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" height={18} />
                  <Skeleton width="90%" height={14} sx={{ mt: 0.5 }} />
                </Box>
              </Box>
            ))
          : (data || []).map((item) => (
              <Box
                key={item.property_id}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  mb: 1.5,
                  p: 1,
                  borderRadius: 1.5,
                  transition: "background-color 0.15s",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <Avatar
                  src={item.primary_image_url || undefined}
                  variant="rounded"
                  sx={{ width: 40, height: 40, fontSize: 14 }}
                >
                  {item.property_name?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{ flex: 1 }}
                    >
                      {item.property_name}
                    </Typography>
                    {item.neighborhood && (
                      <Chip
                        label={item.neighborhood}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" display="block">Ends {item.ends_on || "unknown"} · captured {item.captured_at ? new Date(item.captured_at).toLocaleDateString() : "date unknown"}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.4,
                      mt: 0.25,
                    }}
                  >
                    {item.specials_summary}
                  </Typography>
                </Box>
              </Box>
            ))}
        {!isLoading && (!data || data.length === 0) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            No current specials with verified end dates
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default SpecialsFeed;
